import React, { useRef, useEffect, useState } from "react";
import Icon from "components/Icon";
import formatTime, { formatDate } from "utils/formatTime";
import { getAttachmentUrl, linkify } from "utils/formatChatData";
import { useOnScreen } from "hooks/useOnScreen";
import CustomVideoPlayer from "./CustomVideoPlayer";
import messageIcon from "assets/images/messageIcon.png";
import typingText from "assets/images/typing-message.gif";
import noteIcon from "assets/images/note_icon.png";
import { useCurrentUserContext } from "context/currentUserContext";
import userRoles from "userRoles";
import { saveAs } from "file-saver";
import { useDispatch, useSelector } from "react-redux";
import useSWR from "swr";
import axios from "axios";
import DocumentConvo from "components/DocumentConvo/DocumentConvo";
import { CHAT_CONFIG, viewFor } from "utils/constants";
import { isMobile } from "utils/useragent";
import { wssend } from "stores/socket";
import { updateImageSlider } from "stores/chats/chats";
import ChatFeedback from "./ChatFeedback";
import { formatRedirectUrl } from "utils/general";
import { callReactNativeByParent } from "utils/utils";
import FeedbackInput from "./FeedbackInput";

//Types
type ILocation = {
  latitude: number;
  longitude: number;
};

type ConvoMessageProps = {
  message: any;
  chatId: string;
  isCurrentUserSender: boolean;
  onImageClick: (imageId: string) => void;
  markMessageAsRead: (messageId: string) => void;
  currentUserName: string;
  submitNewMessage: (msg: any) => void;
  currentUserNameType: string;
  currentUserNameId: string;
  isLastMessage: boolean;
  setMsgSentToBot: (msg: any) => void;
  setIsFeedbackSession: (value: boolean) => void;
  msgSentToBot: string;
  clubMessage?: boolean;
};

type ImageMessageProps = {
  message: any;
  isCurrentUserSender: boolean;
  messageId: string;
  attchUrl: string;
  timeString: string;
  msgSource: string;
  onImageClick: (messageId: string, messageType: string) => void;
  text: string;
  currentUserName: string;
  senderType: string;
  msgDeliveryStatus: Array<string>;
  currentUserType: string | null;
  viewFor: string;
  setUrlToSlider?: (urls: string[]) => void;
};

type VideoMessageProps = {
  message: any;
  isCurrentUserSender: boolean;
  attchUrl: string;
  timeString: string;
  msgSource: string;
  text: string;
  currentUserName: string;
  senderType: string;
  messageId: string;
  msgDeliveryStatus: Array<string>;
  currentUserType: string | null;
  viewFor: string;
};

type VoiceMessageProps = {
  message: any;
  isCurrentUserSender: boolean;
  attchUrl: string;
  timeString: string;
  msgSource: string;
  text: string;
  currentUserName: string;
  senderType: string;
  messageId: string;
  msgDeliveryStatus: Array<string>;
  currentUserType: string | null;
  viewFor: string;
};

type DocumentMessageProps = {
  message: any;
  isCurrentUserSender: boolean;
  attchName: string;
  attchUrl: string;
  timeString: string;
  text: string;
  msgSource: string;
  currentUserName: string;
  senderType: string;
  messageId: string;
  msgDeliveryStatus: Array<string>;
  currentUserType: string | null;
  isApp: boolean | string;
  isIOS: boolean | string;
  viewFor: string;
};

type ButtonedtMessageProps = {
  message: any;
  isCurrentUserSender: boolean;
  content: string;
  timeString: string;
  currentUserName: string;
  senderType: string;
  currentUserType: string | null;
  msgSource: string;
  buttonJson: string;
  messageId: string;
  footerData: string;
  msgDeliveryStatus: Array<string>;
  submitNewMessage: (msg: any) => void;
  currentUserNameType: string;
  currentUserNameId: string;
  isLastMessage: boolean;
  setMsgSentToBot: (msg: any) => void;
  viewFor: string;
};

type TextMessageProps = {
  chatId?: string;
  message: any;
  isCurrentUserSender: boolean;
  content: string;
  timeString: string;
  msgSource: string;
  currentUserName: string;
  messageId: string;
  senderType: string;
  footerData: string;
  msgDeliveryStatus: Array<string>;
  currentUserType: string | null;
  viewFor: string;
  clubMessage?: boolean;
  mediaUrls?: string[];
  buttonJson?: {
    actionable_text: string;
    is_default: number | boolean;
    location_required: boolean;
    payload: {
      gogo_message: string;
      url: string;
      message: string;
      title: string;
    };
    type: string;
    uri: string;
  }[];
  isFirstMessage?: boolean;
  isMssgDisabled?: boolean;
  handleCustomerResponse?: (
    mssg: string,
    mssgId: string,
    shouldRedirect: string,
    nativeEventKey: string
  ) => void;
  selectedMssg?: string | null;
  onImageClick?: (messageId: string, messageType: string) => void;
  setUrlToSlider?: (urls: string[]) => void;
};
type NoteMessageProps = {
  message: any;
  addedBy: string;
  content: string;
  timeString: string;
  msgSource: string;
  currentUserName: string;
  messageId: string;
  senderType: string;
  currentUserType: string | null;
  viewFor: string;
};
type Fref = HTMLDivElement | HTMLParagraphElement;

// download helpers
function forceDownload(blob: string, filename: string) {
  blob = blob.trim();
  filename = filename.trim();

  if (!blob && !filename) {
    console.log(
      `Invalid blob and filename. blob::${blob}. filename:: ${filename}`
    );
    return;
  }

  var a = document.createElement("a");
  a.download = filename;
  a.href = blob;
  // For Firefox https://stackoverflow.com/a/32226068
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function downloadResource(url: string, filename: string = ""): void {
  url = url.trim();

  if (!url) {
    console.log(`Invalid url. url::${url}`);
    return;
  }

  filename = url.split("\\").pop()?.split("/").pop() || "";

  if (!filename) {
    console.log("filename not supplied and cannot be derived from url");
    return;
  }

  fetch(url, {
    headers: new Headers({
      Origin: window.location.origin
    }),
    mode: "cors"
  })
    .then((response) => response.blob())
    .then((blob) => {
      let blobUrl = window.URL.createObjectURL(blob);
      forceDownload(blobUrl, filename);
    })
    .catch((e) => console.error(e));
}

const onDownloadClick = (url: string, fileName: string): void => {
  //downloadResource(url, fileName);
  saveAs(url);
};

//Components

// const msgSourceAvatar = (msgSource:string) => {
//   switch (msgSource) {
//     case "WEB/MOBILE":
//       return <img src="/img/web_mobile_icon.png" alt={msgSource} />;
//     case "WHATSAPP":
//       return <img src="/img/whatsapp_icon.png" alt={msgSource} />;
//     case "BOT":
//       return <img src="/img/bot_icon.png" alt={msgSource} />;
//     default:
//       return <img src="/img/web_mobile_icon.png" alt={msgSource} />;
//   }
// };

const ImageMessage = React.forwardRef<Fref, ImageMessageProps>(
  (props, fref): JSX.Element => {
    // const klassName = `chat__msg chat__img-wrapper ${
    //   //!props.isCurrentUserSender ? "chat__msg--rxd" : "chat__msg--sent"
    //   props.senderType !== userRoles.USER ? "chat__msg--sent" : "chat__msg--rxd"
    // }`;
    const chatInfo = useSelector((state: any) => {
      return state?.chatInfoReducer;
    });
    const isWidget = window.location.search.indexOf("containter=widget");

    let klassName = `chat__msg ${
      props.senderType === props.currentUserType
        ? "chat__msg--sent"
        : "chat__msg--rxd"
    }`;

    let rootClass = `msg-wrapper ${
      props.senderType === props.currentUserType ? "msg-sent" : "msg-rxd"
    }`;
    const openImageSlider = () => {
      props?.setUrlToSlider && props.setUrlToSlider([props.attchUrl]);
      props.onImageClick(props.messageId, props.message.type);
    };

    return (
      <div className={rootClass}>
        {/* {props.senderType !== props.currentUserType ? <ChatIcon /> : null} */}
        <div
          className={klassName}
          ref={fref}
          onClick={openImageSlider}
          sender-type={props.senderType}
          id={props.messageId}
        >
          <div
            className={`
              messageWrapper image-message-wrapper ${props.message.senderType}
              ${
                props.senderType !== props.currentUserType
                  ? "messageWrapper--agent"
                  : "messageWrapper--user"
              }
            `}
          >
            {viewFor === "transcript" && (
              <div className="transcript-info">
                <span className="sender-type">
                  {props.message.senderType} -
                </span>{" "}
                {props.message.senderType === "agent"
                  ? chatInfo.chatAgents[props.message.sender]?.agentName
                  : props.message.senderType === "BOT"
                  ? chatInfo.botName
                  : props.message.senderType === "user"
                  ? chatInfo.user["name"]
                  : ""}
              </div>
            )}
            {/* {props.senderType !== userRoles.USER ? (
              <div className={"messageHeader"}>
                <div className={"leftHeader"}>
                  <img src={messageIcon}></img>
                  <span className="agentName">
                    {props.currentUserName || "Agent Name"}
                  </span>
                </div>
                <div className={"rightHeader"}>{props.msgSource}</div>
              </div>
            ) : null} */}
            <div className={"image-wrapper"}>
              {<img src={getAttachmentUrl(props.attchUrl)}></img>}
            </div>
            <div>{props.text}</div>

            {/* {props.senderType !== userRoles.USER ? (
              <DeliveryStatus msgDeliveryStatus={props.msgDeliveryStatus} />
            ) : null} */}
          </div>

          <div
            className={`chat__time ${
              props.senderType === props.currentUserType ? "right-align" : ""
            }`}
          >
            <span className="message-time">{formatTime(props.timeString)}</span>
          </div>
        </div>
      </div>
    );
  }
);

const VideoMessage = React.forwardRef<Fref, VideoMessageProps>(
  (props, fref): JSX.Element => {
    // const klassName = `chat__msg chat__img-wrapper ${
    //   props.senderType !== userRoles.USER ? "chat__msg--sent" : "chat__msg--rxd"
    // }`;
    const chatInfo = useSelector((state: any) => {
      return state?.chatInfoReducer;
    });
    const isWidget = window.location.search.indexOf("containter=widget");

    let klassName = `chat__msg chat__multimedia ${
      props.senderType === props.currentUserType
        ? "chat__msg--sent"
        : "chat__msg--rxd"
    }`;

    let rootClass = `msg-wrapper ${
      props.senderType === props.currentUserType ? "msg-sent" : "msg-rxd"
    }`;

    return (
      <div className={rootClass}>
        {/* {props.senderType !== props.currentUserType ? <ChatIcon /> : null} */}
        <div className={klassName} ref={fref} id={props.messageId}>
          <div
            className={`messageWrapper ${props.message.senderType} ${
              props.senderType !== props.currentUserType
                ? "messageWrapper--agent"
                : "messageWrapper--user"
            }`}
          >
            {viewFor === "transcript" && (
              <div className="transcript-info">
                <span className="sender-type">
                  {props.message.senderType} -
                </span>{" "}
                {props.message.senderType === "agent"
                  ? chatInfo.chatAgents[props.message.sender]?.agentName
                  : props.message.senderType === "BOT"
                  ? chatInfo.botName
                  : props.message.senderType === "user"
                  ? chatInfo.user["name"]
                  : ""}
              </div>
            )}
            {/* {props.senderType !== userRoles.USER ? (
              <div className={"messageHeader"}>
                <div className={"leftHeader"}>
                  <img src={messageIcon}></img>
                  <span className="agentName">
                    {props.currentUserName || "Agent Name"}
                  </span>
                </div>
                <div className={"rightHeader"}>{props.msgSource}</div>
              </div>
            ) : null} */}
            <div className={"video-wrapper"}>
              <CustomVideoPlayer url={getAttachmentUrl(props.attchUrl)} />
            </div>
            <div>{props.text}</div>
            {/* {props.senderType !== userRoles.USER ? (
              <DeliveryStatus msgDeliveryStatus={props.msgDeliveryStatus} />
            ) : null} */}
          </div>

          <div
            className={`chat__time ${
              props.senderType === props.currentUserType ? "right-align" : ""
            }`}
          >
            <span className="message-time">{formatTime(props.timeString)}</span>
          </div>
        </div>
      </div>
    );
  }
);

const VoiceMessage = React.forwardRef<Fref, VoiceMessageProps>(
  (props, fref): JSX.Element => {
    // const klassName = `chat__msg chat__img-wrapper ${
    //   props.senderType !== userRoles.USER ? "chat__msg--sent" : "chat__msg--rxd"
    // }`;
    const chatInfo = useSelector((state: any) => {
      return state?.chatInfoReducer;
    });
    const isWidget = window.location.search.indexOf("containter=widget");

    let klassName = `chat__msg audio-chat-msg ${
      props.senderType === props.currentUserType
        ? "chat__msg--sent"
        : "chat__msg--rxd"
    }`;

    let rootClass = `msg-wrapper ${
      props.senderType === props.currentUserType ? "msg-sent" : "msg-rxd"
    }`;

    return (
      <div className={rootClass}>
        {/* {props.senderType !== props.currentUserType ? <ChatIcon /> : null} */}
        <div className={klassName} ref={fref} id={props.messageId}>
          <div
            className={`messageWrapper ${props.message.senderType} ${
              props.senderType !== props.currentUserType
                ? "messageWrapper--agent"
                : "messageWrapper--user"
            }`}
          >
            {viewFor === "transcript" && (
              <div className="transcript-info">
                <span className="sender-type">
                  {props.message.senderType} -
                </span>{" "}
                {props.message.senderType === "agent"
                  ? chatInfo.chatAgents[props.message.sender]?.agentName
                  : props.message.senderType === "BOT"
                  ? chatInfo.botName
                  : props.message.senderType === "user"
                  ? chatInfo.user["name"]
                  : ""}
              </div>
            )}
            <div className={"video-wrapper"}>
              <audio controls controlsList="nodownload noplaybackrate">
                <source src={props.message.attchUrl} type="audio/ogg" />
                <source src={props.message.attchUrl} type="audio/mpeg" />
                Your browser does not support the audio element.
              </audio>
            </div>
            {/* {props.senderType !== userRoles.USER ? (
              <DeliveryStatus msgDeliveryStatus={props.msgDeliveryStatus} />
            ) : null} */}
          </div>

          <div
            className={`chat__time ${
              props.senderType === props.currentUserType ? "right-align" : ""
            }`}
          >
            <span className="message-time">{formatTime(props.timeString)}</span>
          </div>
        </div>
      </div>
    );
  }
);

const NoteMessage = React.forwardRef<Fref, NoteMessageProps>(
  (props, fref): JSX.Element => {
    // const klassName = `chat__msg chat__note`;
    const chatInfo = useSelector((state: any) => {
      return state?.chatInfoReducer;
    });
    let klassName = `chat__msg chat__note ${
      props.senderType === props.currentUserType
        ? "chat__msg--sent"
        : "chat__msg--rxd"
    }`;

    let rootClass = `msg-wrapper ${
      props.senderType === props.currentUserType ? "msg-sent" : "msg-rxd"
    }`;
    return (
      <div className={rootClass}>
        {/* {props.senderType !== props.currentUserType ? <ChatIcon /> : null} */}
        <div className={klassName} ref={fref} id={props.messageId}>
          <div className="chat__note-bg">
            {viewFor === "transcript" && (
              <div className="transcript-info">
                <span className="sender-type">
                  {props.message.senderType} -
                </span>{" "}
                {props.message.senderType === "agent"
                  ? chatInfo.chatAgents[props.message.sender]?.agentName
                  : props.message.senderType === "BOT"
                  ? chatInfo.botName
                  : props.message.senderType === "user"
                  ? chatInfo.user["name"]
                  : ""}
              </div>
            )}
            <div className="d-flex">
              <div className="chat__note-icon">
                <img src={noteIcon} />
              </div>
              <div className="note__content">
                <div className="note__title">Note Added</div>
                <div
                  dangerouslySetInnerHTML={{ __html: linkify(props.content) }}
                ></div>
              </div>
            </div>
          </div>

          <div
            className={`chat__time ${
              props.senderType === props.currentUserType ? "right-align" : ""
            }`}
          >
            <span className="chat__msg-footer">
              {formatTime(props.timeString)}
            </span>
          </div>
        </div>
      </div>
    );
  }
);
const InfoMessage = React.forwardRef<Fref, NoteMessageProps>(
  (props, fref): JSX.Element => {
    // if (viewFor === "user") {
    //   return <></>;
    // }
    const klassName = `chat__msg chat__note`;
    return (
      <div className="chat__msg-info-root">
        <div className="text-center font-14 chat__msg-info">
          <div>
            <span
              dangerouslySetInnerHTML={{ __html: linkify(props.content) }}
            ></span>
            {/* <span>{" - "}</span> */}
            {/* <span>{formatDate(new Date(props.message.timeStamp))}</span> */}
          </div>
        </div>
        {/* <div className={klassName} ref={fref} id={props.messageId}>
          <div className="chat__note-icon">
            <img src={noteIcon} />
          </div>
          <div className="note__content">
            <div className="note__title">Chat updated!!</div>
            <div
              dangerouslySetInnerHTML={{ __html: linkify(props.content) }}
            ></div>
            <span className="chat__msg-footer">
              {formatTime(props.timeString)}
            </span>
          </div>
        </div> */}
      </div>
    );
  }
);

const DocumentMessage = React.forwardRef<Fref, DocumentMessageProps>(
  (props, fref): JSX.Element => {
    const chatInfo = useSelector((state: any) => {
      return state?.chatInfoReducer;
    });
    const isWidget = window.location.search.indexOf("containter=widget");

    let klassName = `chat__msg ${
      props.senderType === props.currentUserType
        ? "chat__msg--sent"
        : "chat__msg--rxd"
    }`;

    let rootClass = `msg-wrapper ${
      props.senderType === props.currentUserType ? "msg-sent" : "msg-rxd"
    }`;

    const shareFallback = (
      href: string,
      fileName: string,
      fileType: string
    ) => {
      callReactNativeByParent({
        eventKey: "OPEN_SHARE",
        data: {
          shareSingle: false,
          shareOptions: {
            saveToFiles: true,
            type: fileType,
            title: fileName,
            message: href
          }
        }
      });
    };

    const onShare = async (
      href: string,
      fileName: string,
      fileType: string
    ) => {
      fetch(getAttachmentUrl(props.attchUrl))
        .then((response) => response.blob())
        .then(async (res) => {
          const file = new File([res], props?.attchName, {
            type: "application/pdf"
          });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file] }).catch((error) => {
              if (error?.name !== "AbortError") {
                shareFallback(href, fileName, fileType);
              }
            });
          } else {
            shareFallback(href, fileName, fileType);
          }
        })
        .catch((error) => alert(error));
    };

    return (
      <div className={rootClass}>
        {/* {props.senderType !== props.currentUserType ? <ChatIcon /> : null} */}
        <div
          className={klassName}
          ref={fref}
          sender-type={props.senderType}
          id={props.messageId}
        >
          <div
            className={`documentMessageWrapper ${props.message.senderType} ${
              props.senderType !== props.currentUserType
                ? "messageWrapper--agent"
                : "messageWrapper--user"
            }`}
          >
            {viewFor === "transcript" && (
              <div className="transcript-info">
                <span className="sender-type">
                  {props.message.senderType} -
                </span>{" "}
                {props.message.senderType === "agent"
                  ? chatInfo.chatAgents[props.message.sender]?.agentName
                  : props.message.senderType === "BOT"
                  ? chatInfo.botName
                  : props.message.senderType === "user"
                  ? chatInfo.user["name"]
                  : ""}
              </div>
            )}
            {/* {props.senderType !== userRoles.USER ? (
              <div className={"messageHeader"}>
                <div className={"leftHeader"}>
                  <img src={messageIcon}></img>
                  <span className="agentName">
                    {props.currentUserName || "Agent Name"}
                  </span>
                </div>
                <div className={"rightHeader"}>{props.msgSource}</div>
              </div>
            ) : null} */}
            <DocumentConvo url={getAttachmentUrl(props.attchUrl)} />
            <div className={""}>
              <div className="chat__download-wrapper">
                <div className="doc-name">
                  <div
                    style={{
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      position: "relative",
                      overflowX: "hidden",
                      width: "84%",
                      marginRight: 10
                    }}
                  >
                    {props.attchName}
                  </div>
                  <button
                    className="chat__msg-download-option"
                    onClick={() => {
                      console.log("on doc click->>", props);
                      if (props?.isApp) {
                        if (props?.isIOS) {
                          onShare(
                            getAttachmentUrl(props.attchUrl),
                            props.attchName,
                            "application/*"
                          );
                        } else {
                          window.location.href = getAttachmentUrl(
                            props.attchUrl
                          );
                        }
                      } else {
                        onDownloadClick(
                          getAttachmentUrl(props.attchUrl),
                          props.attchName
                        );
                      }
                    }}
                  >
                    <Icon
                      id="downloadAsset"
                      className="char__msg_download-icon"
                      width="20"
                      height="20"
                    />
                  </button>
                </div>
              </div>
            </div>
            {/* <div>{props.text}</div> */}
            {/* {props.senderType !== userRoles.USER ? (
              <DeliveryStatus msgDeliveryStatus={props.msgDeliveryStatus} />
            ) : null} */}
          </div>

          <div
            className={`chat__time ${
              props.senderType === props.currentUserType ? "right-align" : ""
            }`}
          >
            <span className="message-time">{formatTime(props.timeString)}</span>
          </div>
        </div>
      </div>
    );
  }
);

const ButtonedtMessage = React.forwardRef<Fref, ButtonedtMessageProps>(
  (props, fref): JSX.Element => {
    let buttons: any = props.buttonJson;
    const selectedReply = buttons?.selectedReply || "";
    buttons = buttons?.items || buttons?.quick_replies || [];
    const chatInfo = useSelector((state: any) => {
      return state?.chatInfoReducer;
    });

    const isWidget = window.location.search.indexOf("containter=widget");
    let klassName = `${
      props.senderType === props.currentUserType
        ? "chat__msg--sent"
        : "chat__msg--rxd"
    }`;
    let rootClass = `msg-wrapper ${
      props.senderType === props.currentUserType ? "msg-sent" : "msg-rxd"
    }`;

    const [selectedText, setSelectedText] = useState(selectedReply);

    const sendMessgeParams = {
      senderId: props.currentUserNameId,
      senderType: props.currentUserNameType,
      msgType: "freeText",
      type: "text",
      timeStamp: new Date().toISOString(),
      msgSource: "WEB",
      isSelectedButtonOption: true
    };

    const buttonOptionSelected = (text: string) => {
      if (selectedText.length || !props.isLastMessage) return;
      setSelectedText(text);
      props.submitNewMessage({
        ...sendMessgeParams,
        content: text,
        selectedButtonedOption: text,
        isButtonedResponse: true,
        buttonedMessageId: props.messageId
      });
      props.setMsgSentToBot(props.messageId);
    };

    return (
      <div className={rootClass}>
        {/* {props.senderType !== props.currentUserType ? <ChatIcon /> : null} */}
        <div className="chat__msg" style={{ flexDirection: "column" }}>
          <div
            className={klassName}
            ref={fref}
            sender-type={props.senderType}
            id={props.messageId}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div
                className={`messageWrapper ${props.message.senderType} ${
                  props.senderType !== props.currentUserType
                    ? "messageWrapper--agent"
                    : "messageWrapper--user"
                }`}
              >
                {viewFor === "transcript" && (
                  <div className="transcript-info">
                    <span className="sender-type">
                      {props.message.senderType} -
                    </span>{" "}
                    {props.message.senderType === "agent"
                      ? chatInfo.chatAgents[props.message.sender]?.agentName
                      : props.message.senderType === "BOT"
                      ? chatInfo.botName
                      : props.message.senderType === "user"
                      ? chatInfo.user["name"]
                      : ""}
                  </div>
                )}
                {props.senderType !== userRoles.USER && !isWidget ? (
                  <div className={"messageHeader"}>
                    <div className={"leftHeader"}>
                      <img src={messageIcon}></img>
                      <span className="agentName">
                        {props.currentUserName || (chatInfo.botName === "smilecare_bot" ? "smilecare bot" : "Agent Name")}
                      </span>
                    </div>
                    <div className={"rightHeader"}>{props.msgSource}</div>
                  </div>
                ) : null}
                <div className={"content-wrapper"}>
                  <div
                    dangerouslySetInnerHTML={{ __html: linkify(props.content) }}
                  ></div>
                </div>
                <div className="messageFooter">{props?.footerData}</div>
                {/* {props.senderType !== userRoles.USER ? (
                  <DeliveryStatus msgDeliveryStatus={props.msgDeliveryStatus} />
                ) : null} */}
              </div>
              <div
                className={`chat__time ${
                  props.senderType === props.currentUserType
                    ? "right-align"
                    : ""
                }`}
              >
                <span className="message-time">
                  {formatTime(props.timeString)}
                </span>
              </div>
            </div>
          </div>
          <div className="buttoned-options-wrapper">
            {buttons.map((b: any) => {
              const isSelectedClass =
                b.actionable_text === selectedText ? "selected" : "";
              return (
                <span
                  onClick={() => {
                    if (viewFor === "agent" && chatInfo.botMode) {
                      return;
                    }
                    buttonOptionSelected(b.actionable_text);
                  }}
                  className={
                    "buttoned-options " +
                    isSelectedClass +
                    (viewFor === "agent" && chatInfo.botMode
                      ? " buttoned-options-disabled"
                      : "")
                  }
                >
                  {b.actionable_text}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    );
  }
);

const TextMessage = React.forwardRef<Fref, TextMessageProps>(
  (props, fref): JSX.Element => {
    const chatInfo = useSelector((state: any) => {
      return state?.chatInfoReducer;
    });

    const isWidget = window.location.search.indexOf("containter=widget");
    const isMakeoBuddy = props.msgSource === "makeo_buddy";

    let klassName = `chat__msg ${
      props.senderType === props.currentUserType
        ? "chat__msg--sent"
        : "chat__msg--rxd"
    }`;

    let rootClass = `${
      props?.clubMessage ? "msg-wrapper--club" : "msg-wrapper"
    } ${props.senderType === props.currentUserType ? "msg-sent" : "msg-rxd"}`;

    const isFetchingSenderName =
      props.currentUserName.startsWith("Unknown") ||
      props.currentUserName.startsWith("fetching");

    const wrapMediaContent = props?.mediaUrls
      ? props?.mediaUrls?.length > 3
      : false;
    const mediaUrls = wrapMediaContent
      ? props.mediaUrls?.slice(0, 4)
      : props.mediaUrls;

    const openImageSlider = () => {
      props.onImageClick &&
        props.onImageClick(props.messageId, props.message.type);
      props?.setUrlToSlider &&
        props.mediaUrls &&
        props?.setUrlToSlider(props.mediaUrls);
    };

    const sendButtonResponse = (
      text: string,
      mssgId: string,
      shouldRedirect: string,
      nativeEventKey: string
    ) => {
      props?.handleCustomerResponse &&
        props?.handleCustomerResponse(
          text,
          mssgId,
          shouldRedirect,
          nativeEventKey
        );
    };

    return (
      <div className={rootClass}>
        {/* {props.senderType !== props.currentUserType ? <ChatIcon /> : null} */}
        <div
          className={klassName}
          ref={fref}
          sender-type={props.senderType}
          id={props.messageId}
        >
          <div
            className={`messageWrapper ${
              props.senderType !== props.currentUserType
                ? "messageWrapper--agent"
                : "messageWrapper--user"
            }`}
          >
            {viewFor === "transcript" && (
              <div className="transcript-info">
                <span className="sender-type">
                  {props.message.senderType} -
                </span>{" "}
                {props.message.senderType === "agent"
                  ? chatInfo.chatAgents[props.message.sender]?.agentName
                  : props.message.senderType === "BOT"
                  ? chatInfo.botName
                  : props.message.senderType === "user"
                  ? chatInfo.user["name"]
                  : ""}
              </div>
            )}
            {props.senderType !== userRoles.USER && !props?.isFirstMessage ? (
              <div className={"messageHeader"}>
                <div className={"leftHeader"}>
                  <span className="agentName">
                    {!isFetchingSenderName
                      ? props.currentUserName
                      : isMakeoBuddy
                      ? "Dr.makeO"
                      : chatInfo.botName === "smilecare_bot" ? "smilecare bot" : "Agent Name"}
                  </span>
                </div>
                {/* <div className={"rightHeader"}>{props.msgSource}</div> */}
              </div>
            ) : null}
            {props?.isFirstMessage && (
              <div className={"messageHeader"}>
                <img
                  src="https://assets.toothsi.in/Mask_group_b45a54604f.png"
                  alt="#"
                  className="makeo-buddy"
                />
              </div>
            )}
            <div className={"content-wrapper"}>
              <div
                dangerouslySetInnerHTML={{ __html: linkify(props.content) }}
                className={
                  props.senderType !== props.currentUserType
                    ? "message-content-agent"
                    : "message-content-user"
                }
              ></div>
              {chatInfo?.status === "ACTIVE" &&
                props?.message.type === "makeo_feedback_text" &&
                viewFor === "user" && (
                  <FeedbackInput
                    chatId={props?.chatId || ""}
                    mssgId={props?.message?.messageId}
                    isFeedbackSubmitted={props?.message?.feedbackSubmitted}
                  />
                )}
              {props.mediaUrls && (
                <div
                  className={`${
                    wrapMediaContent ? "media-message-wrapper" : ""
                  }`}
                  onClick={openImageSlider}
                >
                  {mediaUrls?.map((url, indx) => (
                    <img
                      key={indx}
                      src={getAttachmentUrl(url)}
                      alt="#"
                      className={`${
                        wrapMediaContent
                          ? "media-message-wrapper__img"
                          : "media-message-wrapper__img--normal"
                      }`}
                    />
                  ))}
                  {wrapMediaContent && (
                    <>
                      <div className="media-message-cover">
                        <div className="media-message-count">
                          <span className="media-message">
                            +{props?.mediaUrls?.length}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              {props.buttonJson && props?.selectedMssg === null && (
                <div className="chat-option">
                  {props.buttonJson.map((option, indx) => (
                    <button
                      type="button"
                      className="chat-option__btn"
                      key={indx}
                      onClick={() => {
                        sendButtonResponse(
                          option.actionable_text,
                          props.messageId,
                          option?.payload?.url,
                          option?.payload?.gogo_message
                        );
                      }}
                      disabled={props.isMssgDisabled}
                    >
                      {option?.actionable_text}

                      {(option?.payload?.url ||
                        option?.payload?.gogo_message) && (
                        <span>
                          {" "}
                          <Icon id="redirect" />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="messageFooter">{props?.footerData}</div>

            {/* {props.senderType !== userRoles.USER ? (
              <DeliveryStatus msgDeliveryStatus={props.msgDeliveryStatus} />
            ) : null} */}
            {/* {<DeliveryStatus msgDeliveryStatus={props.msgDeliveryStatus} />} */}
          </div>

          {!props?.clubMessage && (
            <div
              className={`chat__time ${
                props.senderType === props.currentUserType ? "right-align" : ""
              }`}
            >
              <span className="message-time">
                {props.timeString ? formatTime(props.timeString) : null}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
);

const Typing = React.forwardRef<Fref>((props, fref): JSX.Element => {
  const isWidget = window.location.search.indexOf("containter=widget");

  let klassName = `chat__msg chat__msg--rxd typing`;

  let rootClass = `msg-wrapper msg-rxd"`;

  return (
    <div className={rootClass}>
      <ChatIcon />
      <div className={klassName} ref={fref}>
        <img src={typingText} />
      </div>
    </div>
  );
});

const LocationMessage = React.forwardRef<Fref, TextMessageProps>(
  (props, fref): JSX.Element => {
    const chatInfo = useSelector((state: any) => {
      return state?.chatInfoReducer;
    });
    let klassName = `chat__msg ${
      props.senderType === props.currentUserType
        ? "chat__msg--sent"
        : "chat__msg--rxd"
    }`;

    let rootClass = `msg-wrapper ${
      props.senderType === props.currentUserType ? "msg-sent" : "msg-rxd"
    }`;

    const locationPrefix = `https://www.google.com/maps/search/?api=1&query=`;
    const coordinates: ILocation = JSON.parse(props.content);
    let locationLink: string | boolean = false;

    if (coordinates.latitude && coordinates.longitude) {
      locationLink = `${locationPrefix}${coordinates.latitude},${coordinates.longitude}`;
    }

    return (
      <div className={rootClass}>
        {/* {props.senderType !== props.currentUserType ? <ChatIcon /> : null} */}
        <div
          className={klassName}
          ref={fref}
          sender-type={props.senderType}
          id={props.messageId}
        >
          <div
            className={`messageWrapper ${props.message.senderType} ${
              props.senderType !== props.currentUserType
                ? "messageWrapper--agent"
                : "messageWrapper--user"
            }`}
          >
            {viewFor === "transcript" && (
              <div className="transcript-info">
                <span className="sender-type">
                  {props.message.senderType} -
                </span>{" "}
                {props.message.senderType === "agent"
                  ? chatInfo.chatAgents[props.message.sender]?.agentName
                  : ""}
                {props.message.senderType === "user"
                  ? chatInfo.user["name"]
                  : ""}
              </div>
            )}
            {props.senderType !== userRoles.USER ? (
              <div className={"messageHeader"}>
                <div className={"leftHeader"}>
                  <img src={messageIcon}></img>
                  <span className="agentName">
                    {props.currentUserName || (chatInfo.botName === "smilecare_bot" ? "smilecare bot" : "Agent Name")}
                  </span>
                </div>
                <div className={"rightHeader"}>{props.msgSource}</div>
              </div>
            ) : null}
            <div className={"content-wrapper"}>
              <div>
                {locationLink === false ? (
                  "Invalid Location. Missing latitude or longitude"
                ) : (
                  <a
                    className="location-link"
                    href={locationLink}
                    target="_blank"
                  >
                    Location
                  </a>
                )}
              </div>
            </div>
            {/* {props.senderType !== userRoles.USER ? (
              <DeliveryStatus msgDeliveryStatus={props.msgDeliveryStatus} />
            ) : null} */}
          </div>

          <div
            className={`chat__time ${
              props.senderType === props.currentUserType ? "right-align" : ""
            }`}
          >
            <span className="message-time">{formatTime(props.timeString)}</span>
          </div>
        </div>
      </div>
    );
  }
);

export const ConvoMessage = ({
  message,
  chatId,
  isCurrentUserSender,
  onImageClick,
  markMessageAsRead,
  currentUserName,
  submitNewMessage,
  currentUserNameType,
  currentUserNameId,
  isLastMessage,
  setMsgSentToBot,
  clubMessage = false,
  setIsFeedbackSession
}: ConvoMessageProps): any => {
  const { data, error } = useSWR(
    `/api/v2/chatapp/user-chat/getAgentById?id=${message.sender}`,
    async (apiUrl: string): Promise<string> => {
      if (message.senderType != "user") {
        if (chatInfo.chatAgents[message.sender]) {
          return chatInfo.chatAgents[message.sender].agentName;
        } else {
          let res = await axios.get(apiUrl);
          if (res.data) return res.data[message.sender].agentName;
        }
      }
      return "";
    },
    { revalidateOnFocus: false }
  );
  const messageRef = useRef<HTMLDivElement | HTMLParagraphElement | null>(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  let onScreen = useOnScreen(messageRef);
  const isMarked = useRef(false);
  const chatInfo = useSelector((state: any) => {
    return state?.chatInfoReducer;
  });
  const dispatch = useDispatch();
  const { currentUser } = useCurrentUserContext();
  const [senderName, setSenderName] = useState("");
  const [disableButton, setDisableButton] = useState(false);

  function getSenderName(): string {
    if (error) return `Unknown ${message.sender}`;
    if (!data && chatInfo?.currentAgent?.name)
      return chatInfo?.currentAgent?.name;
    if (!data) return "Fetching name...";
    return data;
  }

  function getSenderType(message: any) {
    return viewFor === "agent" && message.senderType === "BOT"
      ? "agent"
      : message.senderType;
  }
  const [isIOS, setisIOS] = useState<any>(false);
  const isApp = isMobile();

  useEffect(() => {
    setisIOS(!!navigator.userAgent.includes("iPhone"));
  }, []);

  const handleCustomerResponse = async (
    mssg: string,
    mssgId: string,
    shouldRedirect: string,
    nativeEventKey: string
  ) => {
    setDisableButton(true);
    const newMessageObj = {
      buttonedMessageId: mssgId,
      senderId: currentUser?.id,
      senderType: viewFor,
      msgType: "freeText",
      type: "text",
      content: mssg,
      isButtonedResponse: true,
      isSelectedButtonOption: true,
      selectedButtonedOption: mssg,
      timeStamp: new Date().toISOString(),
      msgSource: "WEB",
      chatId: chatId
    };
    dispatch(
      wssend(
        // @ts-ignore
        JSON.stringify({
          action: "addNewMessage",
          data: {
            chatId: chatId,
            chat: newMessageObj
          }
        })
      )
    );
    try {
      const response = await axios.post(
        "/api/v2/chatapp/makeo/update-bot-flow",
        {
          selection: mssg,
          chatId: chatId,
          messageId: message?.messageId
        }
      );
      if (response?.data?.isSuccess && shouldRedirect) {
        if (!shouldRedirect.includes("@@base_domain@@")) {
          callReactNativeByParent({
            eventKey: "EXTERNAL_URL_OPEN",
            data: { url: shouldRedirect }
          });
          return;
        }
        const baseDomainUrl = formatRedirectUrl(shouldRedirect);
        const hasQuery = baseDomainUrl.includes("?");
        const message = JSON.stringify({
          key: "HANDLE_REDIRECT",
          pathName: baseDomainUrl,
          chatId,
          append: hasQuery ? "&" : "?"
        });

        window.parent.postMessage(message, "*");
      } else if (
        response?.data?.isSuccess &&
        nativeEventKey &&
        nativeEventKey === "HOW_TO_USE"
      ) {
        callReactNativeByParent({
          eventKey: "HOW_TO_USE",
          data: {}
        });
      }
    } catch (error) {
      console.log("api request failed: ", error);
    }
  };

  const openImageSlider = (messageId: string, messageType: string) => {
    dispatch(
      updateImageSlider({
        isOpen: true,
        messageId,
        messageType
      })
    );
  };
  const setUrlToSlider = (urls: string[]) => {
    dispatch(
      updateImageSlider({
        imageUrls: urls
      })
    );
    const message = JSON.stringify({
      key: "HIDE_HEADER"
    });
    window.parent.postMessage(message, "*");
  };

  useEffect(() => {
    let cond =
      onScreen &&
      markMessageAsRead &&
      !isCurrentUserSender &&
      !isMarked.current &&
      !message.isReadByReceiver; //message is not read by current user

    if (cond) {
      markMessageAsRead(message.messageId);
      isMarked.current = true;
    }
  }, [onScreen, markMessageAsRead]);
  useEffect(() => {
    if (!chatInfo) return;
    if (message.senderType != "user") {
      try {
        setSenderName(
          chatInfo.chatAgents[message.sender]
            ? chatInfo.chatAgents[message.sender].agentName
            : "Admin"
        );
      } catch (error) {}
    }
  }, []);

  switch (message.type) {
    case "voice":
      return (
        <VoiceMessage
          isCurrentUserSender={isCurrentUserSender}
          attchUrl={message.attchUrl}
          timeString={message.timeString}
          ref={messageRef}
          text={message.content}
          msgSource={message.msgSource}
          currentUserName={
            message.senderType === "agent"
              ? getSenderName()
              : currentUser?.name || "User"
          }
          senderType={getSenderType(message)}
          messageId={message.messageId}
          msgDeliveryStatus={message.deliveryStatus}
          currentUserType={viewFor === "transcript" ? "agent" : viewFor}
          viewFor={viewFor}
          message={message}
        />
      );
    case "image":
      return (
        <ImageMessage
          isCurrentUserSender={isCurrentUserSender}
          messageId={message.messageId}
          attchUrl={message.attchUrl}
          timeString={message.timeString}
          onImageClick={openImageSlider}
          msgSource={message.msgSource}
          ref={messageRef}
          text={message.content}
          currentUserName={
            message.senderType === "agent"
              ? getSenderName()
              : currentUser?.name || "User"
          }
          senderType={getSenderType(message)}
          msgDeliveryStatus={message.deliveryStatus}
          currentUserType={viewFor === "transcript" ? "agent" : viewFor}
          viewFor={viewFor}
          message={message}
          setUrlToSlider={setUrlToSlider}
        />
      );
    case "video":
      return (
        <VideoMessage
          isCurrentUserSender={isCurrentUserSender}
          attchUrl={message.attchUrl}
          timeString={message.timeString}
          ref={messageRef}
          text={message.content}
          msgSource={message.msgSource}
          currentUserName={
            message.senderType === "agent"
              ? getSenderName()
              : currentUser?.name || "User"
          }
          senderType={getSenderType(message)}
          messageId={message.messageId}
          msgDeliveryStatus={message.deliveryStatus}
          currentUserType={viewFor === "transcript" ? "agent" : viewFor}
          viewFor={viewFor}
          message={message}
        />
      );
      break;
    case "document":
      return (
        <DocumentMessage
          isCurrentUserSender={isCurrentUserSender}
          attchUrl={message.attchUrl}
          timeString={message.timeString}
          attchName={message.attchName}
          ref={messageRef}
          msgSource={message.msgSource}
          text={message.content}
          currentUserName={
            message.senderType === "agent"
              ? getSenderName()
              : currentUser?.name || "User"
          }
          senderType={getSenderType(message)}
          messageId={message.messageId}
          msgDeliveryStatus={message.deliveryStatus}
          currentUserType={viewFor === "transcript" ? "agent" : viewFor}
          viewFor={viewFor}
          message={message}
          isApp={isApp}
          isIOS={isIOS}
        />
      );
    case "note":
      return (
        <NoteMessage
          addedBy={message.senderName}
          timeString={message.timeString}
          content={message.content}
          msgSource={message.msgSource}
          ref={messageRef}
          currentUserName={
            message.senderType === "agent"
              ? getSenderName()
              : currentUser?.name || "User"
          }
          messageId={message.messageId}
          senderType={getSenderType(message)}
          currentUserType={viewFor === "transcript" ? "agent" : viewFor}
          viewFor={viewFor}
          message={message}
        />
      );
    case "info":
      return (
        <InfoMessage
          addedBy={message.senderName}
          timeString={message.timeString}
          content={message.content}
          msgSource={message.msgSource}
          ref={messageRef}
          currentUserName={
            message.senderType === "agent"
              ? getSenderName()
              : currentUser?.name || "User"
          }
          messageId={message.messageId}
          senderType={getSenderType(message)}
          currentUserType={viewFor === "transcript" ? "agent" : viewFor}
          viewFor={viewFor}
          message={message}
        />
      );
    case "location":
      return (
        <LocationMessage
          isCurrentUserSender={isCurrentUserSender}
          timeString={message.timeString}
          content={message.content}
          msgSource={message.msgSource}
          ref={messageRef}
          currentUserName={
            message.senderType === "agent"
              ? getSenderName()
              : currentUser?.name || "User"
          }
          messageId={message.messageId}
          senderType={getSenderType(message)}
          footerData={message.footerData}
          msgDeliveryStatus={message.deliveryStatus}
          currentUserType={viewFor === "transcript" ? "agent" : viewFor}
          viewFor={viewFor}
          message={message}
        />
      );
    case "button":
      return (
        <ButtonedtMessage
          isCurrentUserSender={isCurrentUserSender}
          timeString={message.timeString}
          content={message.content}
          msgSource={message.msgSource}
          ref={messageRef}
          currentUserName={
            message.senderType === "agent"
              ? getSenderName()
              : currentUser?.name || "User"
          }
          messageId={message.messageId}
          senderType={getSenderType(message)}
          footerData={message.footerData}
          msgDeliveryStatus={message.deliveryStatus}
          currentUserType={viewFor === "transcript" ? "agent" : viewFor}
          viewFor={viewFor}
          message={message}
          buttonJson={message.buttonJson}
          submitNewMessage={submitNewMessage}
          currentUserNameType={currentUserNameType}
          currentUserNameId={currentUserNameId}
          isLastMessage={isLastMessage}
          setMsgSentToBot={setMsgSentToBot}
        />
      );
    case "typing":
      return <Typing />;

    case "feedback_v2":
      if (viewFor === "user") {
        return (
          <ChatFeedback
            isFeedbackSubmitted={message?.feedbackSubmitted || null}
            chatId={chatId}
            messageId={message?.messageId}
            isMakeoBuddy={
              chatInfo?.botMode && chatInfo?.botName === "makeo_buddy"
            }
            setIsFeedbackSession={setIsFeedbackSession}
          />
        );
      } else {
        return <></>;
      }
    default:
      return (
        <TextMessage
          chatId={chatId}
          isCurrentUserSender={isCurrentUserSender}
          timeString={message.timeString}
          content={message.content}
          msgSource={message.msgSource}
          ref={messageRef}
          currentUserName={getSenderName()}
          messageId={message.messageId}
          senderType={getSenderType(message)}
          footerData={message.footerData}
          msgDeliveryStatus={message.deliveryStatus}
          currentUserType={viewFor === "transcript" ? "agent" : viewFor}
          viewFor={viewFor}
          message={message}
          clubMessage={clubMessage}
          isFirstMessage={message?.isFirstMsg}
          buttonJson={message?.buttonJson?.items}
          handleCustomerResponse={handleCustomerResponse}
          mediaUrls={message.mediaUrls}
          selectedMssg={message.selectedMsg || null}
          onImageClick={openImageSlider}
          setUrlToSlider={setUrlToSlider}
          isMssgDisabled={disableButton}
        />
      );
  }
};

const DeliveryStatus = function (props: any) {
  if (props.msgDeliveryStatus) {
    if (props.msgDeliveryStatus.includes("READ")) {
      return (
        <span className="deliveryStatus messageRead">
          <Icon id="doubleTick" />
        </span>
      );
    } else if (props.msgDeliveryStatus.includes("DELIVERED")) {
      return (
        <span className="deliveryStatus">
          <Icon id="doubleTick" />
        </span>
      );
    } else if (props.msgDeliveryStatus.includes("SENT")) {
      return (
        <span className="deliveryStatus">
          <Icon id="singleTick" />
        </span>
      );
    }
  }

  return null;
};

const ChatIcon = function (props: any) {
  // if (window.location.search.indexOf("botName") > -1) {
  const chatInfo = useSelector((state: any) => {
    return state.chatInfoReducer;
  });

  return (
    <>
      {/* {chatInfo.botName ? ( */}
      <div className="avatar-icon">
        <div className="chat-icon-logo">
          {chatInfo.botName
            ? CHAT_CONFIG.user[chatInfo.botName].logoIcon
            : chatInfo.businessType.substring(0, 1).toUpperCase()}
        </div>
      </div>
      {/* ) : null} */}
    </>
  );
};
